/**
 * Board store — the poll loop, optimistic completions, and the UI state that
 * has to survive a re-render (fold open/closed, the compose picks).
 *
 * Write posture (write-door §6): the board ticks optimistically, and on any
 * non-2xx or network failure it **reverts visibly** and says so. No offline
 * queue, ever.
 */

import { ApiError, BoardApi, uuid } from './api';
import { now } from './clock';
import { pollDelayMs } from './freshness';
import type { BoardState, ComposeItemInput, StateData } from './types';

export interface Flash {
  id: string;
  text: string;
  kind: 'error' | 'info';
}

export interface UiState {
  /** fold id -> open. Survives re-render and reload (localStorage). */
  folds: Record<string, boolean>;
  /** the compose sheet is open (monitor + laptop only). */
  composing: boolean;
  /** picks in morning-pick order. */
  picks: ComposeItemInput[];
  /** control keys with a write in flight. */
  pending: Set<string>;
  /** control keys whose write just failed — the visible revert mark. */
  failed: Set<string>;
  flashes: Flash[];
  /** the poll itself failed: keep the last render, show one global banner. */
  disconnected: boolean;
  /** never composed today AND the surface can't compose (phone). */
  narrow: boolean;
}

const FOLD_KEY = 'life-os.folds';

const DEFAULT_FOLDS: Record<string, boolean> = {
  plan: true,
  shelf: true,
  goals: true,
  habits: true,
};

export type Listener = () => void;

export class BoardStore {
  readonly api: BoardApi;
  state: BoardState | null = null;
  ui: UiState = {
    folds: loadFolds(),
    composing: false,
    picks: [],
    pending: new Set(),
    failed: new Set(),
    flashes: [],
    disconnected: false,
    narrow: false,
  };

  private listeners = new Set<Listener>();
  private timer: number | null = null;

  constructor(api: BoardApi) {
    this.api = api;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  /* ------------------------------------------------------------- polling */

  start(): void {
    void this.refresh(true);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refresh(true);
    });
    this.schedule();
  }

  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    // The board's clock, so a dev-forced 22:30 tapers like a real one.
    const delay = pollDelayMs(now());
    // Stopped overnight: re-check in 15 min rather than dying for good.
    this.timer = setTimeout(() => {
      if (delay !== null && document.visibilityState === 'visible') void this.refresh(false);
      this.schedule();
    }, delay ?? 15 * 60_000) as unknown as number;
  }

  async refresh(withRevalidate: boolean): Promise<void> {
    try {
      const next = await this.api.boardState(withRevalidate);
      this.state = next;
      this.ui.disconnected = false;
      // Mode heartbeat rides alongside the poll while Mode is on.
      if (next.data.state.data?.mode.isOn) void this.api.heartbeat().catch(() => undefined);
    } catch (e) {
      if (e instanceof ApiError && e.isAuthExpired) {
        location.reload(); // expired Access session -> back into the OTP flow
        return;
      }
      this.ui.disconnected = true;
    }
    this.emit();
  }

  /* --------------------------------------------------- optimistic writes */

  private stateData(): StateData | null {
    return this.state?.data.state.data ?? null;
  }

  /** Apply, call, revert on failure — the whole write posture in one place. */
  private async write(key: string, apply: () => () => void, call: () => Promise<unknown>, failText: string): Promise<void> {
    const revert = apply();
    this.ui.pending.add(key);
    this.ui.failed.delete(key);
    this.emit();
    try {
      await call();
      this.ui.pending.delete(key);
      this.emit();
      // Reconcile against the server (streaks, spotlight advance, write-through).
      void this.refresh(false);
    } catch (e) {
      revert();
      this.ui.pending.delete(key);
      this.ui.failed.add(key);
      this.flash(`${failText} — ${describe(e)}`, 'error');
      setTimeout(() => {
        this.ui.failed.delete(key);
        this.emit();
      }, 6000);
      this.emit();
    }
  }

  toggleHabit(habitId: string, done: boolean): void {
    const key = `habit:${habitId}`;
    void this.write(
      key,
      () => {
        const s = this.stateData();
        const h = s?.habits[habitId];
        if (!h) return () => undefined;
        const before = { ...h, checkedDates: [...h.checkedDates] };
        const today = this.state!.data.today;
        h.checkedToday = done;
        h.checkedDates = done
          ? Array.from(new Set([...h.checkedDates, today])).sort()
          : h.checkedDates.filter((d) => d !== today);
        h.streak = Math.max(0, h.streak + (done ? 1 : -1));
        return () => {
          Object.assign(h, before);
        };
      },
      () => this.api.check({ kind: 'habit', id: habitId, done, forDate: 'today' }),
      'Habit check did not take',
    );
  }

  togglePlanItem(itemId: string, done: boolean): void {
    const key = `planItem:${itemId}`;
    void this.write(
      key,
      () => {
        const item = this.stateData()?.plan?.items.find((i) => i.id === itemId);
        if (!item) return () => undefined;
        const before = item.checkedAt;
        item.checkedAt = done ? new Date().toISOString() : null;
        return () => {
          item.checkedAt = before;
        };
      },
      () => this.api.check({ kind: 'planItem', id: itemId, done }),
      'Day-plan tick did not take',
    );
  }

  /** A due reminder that is not on the plan: pure write-through to ClickUp. */
  toggleReminder(reminderId: string, done: boolean): void {
    const key = `reminder:${reminderId}`;
    void this.write(
      key,
      () => {
        const r = this.state?.data.reminders.data.due.find((x) => x.id === reminderId);
        if (!r) return () => undefined;
        const before = r.done;
        r.done = done;
        return () => {
          r.done = before;
        };
      },
      () => this.api.check({ kind: 'reminder', id: reminderId, done }),
      'Reminder did not write through to ClickUp',
    );
  }

  toggleMode(): void {
    const s = this.stateData();
    if (!s) return;
    const next = !s.mode.isOn;
    void this.write(
      'mode',
      () => {
        const before = { ...s.mode };
        s.mode.isOn = next;
        s.mode.since = next ? new Date().toISOString() : null;
        s.mode.elapsedS = 0;
        return () => {
          s.mode = before;
        };
      },
      () =>
        // No `actuator`: the write door derives source from the credential,
        // and reserves that field for the script class (mac-agent | phone).
        // Sending it from the board is rejected as `unknown_actuator`.
        this.api.setMode({
          clientUuid: uuid(),
          state: next ? 'on' : 'off',
          clientAt: new Date().toISOString(),
        }),
      'Mode toggle did not take',
    );
  }

  /* --------------------------------------------------------- the compose */

  openCompose(): void {
    this.ui.composing = true;
    this.emit();
  }

  closeCompose(): void {
    this.ui.composing = false;
    this.emit();
  }

  addPick(item: ComposeItemInput): void {
    const same = (a: ComposeItemInput, b: ComposeItemInput) =>
      a.sourceKind === b.sourceKind && (a.sourceRef ?? null) === (b.sourceRef ?? null) && a.label === b.label;
    if (this.ui.picks.some((p) => same(p, item))) return;
    this.ui.picks.push(item);
    this.emit();
  }

  removePickAt(index: number): void {
    this.ui.picks.splice(index, 1);
    this.emit();
  }

  movePick(index: number, delta: number): void {
    const to = index + delta;
    if (to < 0 || to >= this.ui.picks.length) return;
    const [item] = this.ui.picks.splice(index, 1);
    this.ui.picks.splice(to, 0, item);
    this.emit();
  }

  async submitCompose(): Promise<void> {
    if (!this.state) return;
    const planDate = this.state.data.today;
    const items = this.ui.picks;
    this.ui.pending.add('compose');
    this.emit();
    try {
      await this.api.compose({ clientUuid: uuid(), planDate, items });
      this.ui.composing = false;
      this.ui.picks = [];
      this.flash('Day composed.', 'info');
    } catch (e) {
      this.flash(`Compose did not take — ${describe(e)}`, 'error');
    } finally {
      this.ui.pending.delete('compose');
      await this.refresh(false);
    }
  }

  /* --------------------------------------------------------------- misc */

  toggleFold(id: string): void {
    this.ui.folds[id] = !(this.ui.folds[id] ?? true);
    saveFolds(this.ui.folds);
    this.emit();
  }

  flash(text: string, kind: Flash['kind']): void {
    const f: Flash = { id: uuid(), text, kind };
    this.ui.flashes.push(f);
    setTimeout(() => {
      this.ui.flashes = this.ui.flashes.filter((x) => x.id !== f.id);
      this.emit();
    }, 7000);
    this.emit();
  }
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'unreachable') return 'the write door is unreachable';
    if (e.code === 'clickup_unreachable') return 'ClickUp is unreachable';
    if (e.code === 'plan_expired') return "that plan is not today's";
    return e.code;
  }
  return e instanceof Error ? e.message : String(e);
}

function loadFolds(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(FOLD_KEY);
    return raw ? { ...DEFAULT_FOLDS, ...(JSON.parse(raw) as Record<string, boolean>) } : { ...DEFAULT_FOLDS };
  } catch {
    return { ...DEFAULT_FOLDS };
  }
}

function saveFolds(folds: Record<string, boolean>): void {
  try {
    localStorage.setItem(FOLD_KEY, JSON.stringify(folds));
  } catch {
    /* private mode / kiosk with storage off — folds just stop persisting */
  }
}
